import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Loader2, Sparkles, Send, CalendarPlus, CalendarX2 } from 'lucide-react';
import ScheduleList from './ScheduleList';
import {
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  toggleSchedule,
  aiParseSchedule,
} from '@/services/PagesServices/ReportSchedulerService';
import type { ReportSchedule, CreateSchedulePayload, ScheduleFrequency } from '@/types/reportScheduler';
import './ReportSchedulerModal.css';

interface ReportSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'once', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays (Mon-Fri)' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Dates' },
];

const TIMEZONES = [
  'Asia/Kolkata',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];

const AI_EXAMPLE_COMMANDS = [
  'Every Monday and Friday at 9am',
  'Send report on 1st and 15th of every month at 10am',
  'Daily at 5pm to team@company.com',
  'Every other Wednesday at 2:30pm until Dec 2026',
  'Weekdays at 8am',
];

const ReportSchedulerModal: React.FC<ReportSchedulerModalProps> = ({ isOpen, onClose }) => {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [scheduleName, setScheduleName] = useState('');
  const [frequency, setFrequency] = useState<ScheduleFrequency>('weekly');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]);
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>([1]);
  const [specificDates, setSpecificDates] = useState<string[]>([]);
  const [dateInput, setDateInput] = useState('');
  const [repeatEndDate, setRepeatEndDate] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [error, setError] = useState('');

  // AI Assist state
  const [aiCommand, setAiCommand] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);

  const resetForm = () => {
    setEditingId(null);
    setScheduleName('');
    setFrequency('weekly');
    setDaysOfWeek([1]);
    setDaysOfMonth([1]);
    setSpecificDates([]);
    setDateInput('');
    setRepeatEndDate('');
    setTimeOfDay('09:00');
    setTimezone('Asia/Kolkata');
    setEmailInput('');
    setRecipients([]);
    setError('');
    setAiCommand('');
    setAiExplanation('');
  };

  const loadSchedules = useCallback(async () => {
    setListLoading(true);
    try {
      const result = await getSchedules();
      let items: ReportSchedule[] = [];
      if (Array.isArray(result?.data)) {
        items = result.data;
      } else if (result?.data && Array.isArray((result.data as any).data)) {
        items = (result.data as any).data;
      } else if (Array.isArray(result)) {
        items = result as unknown as ReportSchedule[];
      }
      setSchedules(items);
    } catch (err: any) {
      console.error('Failed to load schedules:', err);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadSchedules();
      resetForm();
    }
  }, [isOpen, loadSchedules]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // --- Email helpers ---
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) { setError('Please enter a valid email address'); return; }
    if (recipients.includes(email)) { setError('Email already added'); return; }
    setRecipients((prev) => [...prev, email]);
    setEmailInput('');
    setError('');
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addEmail(); }
  };

  const removeEmail = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
  };

  // --- Day of Week toggle ---
  const toggleDayOfWeek = (day: number) => {
    setDaysOfWeek((prev) => {
      if (prev.includes(day)) {
        return prev.length > 1 ? prev.filter((d) => d !== day) : prev;
      }
      return [...prev, day].sort();
    });
  };

  // --- Day of Month toggle ---
  const toggleDayOfMonth = (day: number) => {
    setDaysOfMonth((prev) => {
      if (prev.includes(day)) {
        return prev.length > 1 ? prev.filter((d) => d !== day) : prev;
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  // --- Specific date helpers ---
  const addSpecificDate = () => {
    if (!dateInput) return;
    if (specificDates.includes(dateInput)) {
      setError('Date already added');
      return;
    }
    setSpecificDates((prev) => [...prev, dateInput].sort());
    setDateInput('');
    setError('');
  };

  const removeSpecificDate = (date: string) => {
    setSpecificDates((prev) => prev.filter((d) => d !== date));
  };

  // --- AI Assist ---
  const handleAiParse = async () => {
    if (!aiCommand.trim()) return;
    setAiLoading(true);
    setAiExplanation('');
    setError('');

    try {
      const data = await aiParseSchedule(aiCommand.trim());

      // Apply parsed values to form
      if (data.scheduleName) setScheduleName(data.scheduleName);
      if (data.frequency) setFrequency(data.frequency);
      if (data.daysOfWeek?.length) setDaysOfWeek(data.daysOfWeek);
      if (data.daysOfMonth?.length) setDaysOfMonth(data.daysOfMonth);
      if (data.specificDates?.length) setSpecificDates(data.specificDates);
      if (data.repeatEndDate) setRepeatEndDate(data.repeatEndDate);
      if (data.timeOfDay) setTimeOfDay(data.timeOfDay);
      if (data.timezone) setTimezone(data.timezone);
      if (data.recipients?.length) {
        setRecipients((prev) => {
          const merged = new Set([...prev, ...(data.recipients || [])]);
          return Array.from(merged);
        });
      }
      if (data.explanation) setAiExplanation(data.explanation);
    } catch (err: any) {
      console.error('AI parse failed:', err);
      setError('Failed to parse command. Please try rephrasing.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiParse();
    }
  };

  // --- Save ---
  const handleSave = async () => {
    if (recipients.length === 0) {
      setError('Add at least one recipient email');
      return;
    }
    if ((frequency === 'once' || frequency === 'custom') && specificDates.length === 0) {
      setError('Add at least one date');
      return;
    }
    setError('');
    setSaving(true);

    const payload: CreateSchedulePayload = {
      scheduleName: scheduleName || undefined,
      frequency,
      daysOfWeek: (frequency === 'weekly' || frequency === 'biweekly') ? daysOfWeek : undefined,
      daysOfMonth: frequency === 'monthly' ? daysOfMonth : undefined,
      specificDates: (frequency === 'once' || frequency === 'custom') ? specificDates : undefined,
      repeatEndDate: repeatEndDate || null,
      timeOfDay,
      timezone,
      recipients,
    };

    try {
      if (editingId) {
        await updateSchedule(editingId, payload);
      } else {
        await createSchedule(payload);
      }
      resetForm();
      await loadSchedules();
    } catch (err) {
      console.error('Failed to save schedule:', err);
      setError('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: number, isActive: boolean) => {
    try {
      await toggleSchedule(id, isActive);
      setSchedules((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_active: isActive } : s))
      );
    } catch (err) {
      console.error('Failed to toggle schedule:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSchedule(id);
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) resetForm();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
    }
  };

  const handleEdit = (schedule: ReportSchedule) => {
    setEditingId(schedule.id);
    setScheduleName(schedule.schedule_name || '');
    setFrequency(schedule.frequency);
    setDaysOfWeek(schedule.days_of_week || (schedule.day_of_week != null ? [schedule.day_of_week] : [1]));
    setDaysOfMonth(schedule.days_of_month || (schedule.day_of_month != null ? [schedule.day_of_month] : [1]));
    setSpecificDates(schedule.specific_dates || []);
    setRepeatEndDate(schedule.repeat_end_date || '');
    const timeParts = schedule.time_of_day?.split(':') || ['09', '00'];
    setTimeOfDay(`${timeParts[0]}:${timeParts[1]}`);
    setTimezone(schedule.timezone || 'Asia/Kolkata');
    setRecipients(schedule.recipients || []);
    setEmailInput('');
    setError('');
    setAiExplanation('');
  };

  if (!isOpen) return null;

  const formatDateDisplay = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div
        className="rs-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rs-modal-title"
      >
        {/* Header */}
        <div className="rs-modal-header">
          <h2 id="rs-modal-title" className="rs-modal-title">
            {editingId ? 'Edit Report Schedule' : 'Schedule Report Delivery'}
          </h2>
          <button className="rs-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="rs-modal-body">
          {/* AI Assist Panel */}
          <div className="rs-ai-section">
            <button
              className={`rs-ai-toggle ${showAiPanel ? 'rs-ai-toggle-active' : ''}`}
              onClick={() => setShowAiPanel(!showAiPanel)}
              type="button"
            >
              <Sparkles size={16} />
              AI Assist - Describe your schedule in plain English
            </button>

            {showAiPanel && (
              <div className="rs-ai-panel">
                <div className="rs-ai-input-row">
                  <textarea
                    className="rs-ai-textarea"
                    placeholder='e.g., "Send report every Monday and Friday at 9am to hr@company.com"'
                    value={aiCommand}
                    onChange={(e) => setAiCommand(e.target.value)}
                    onKeyDown={handleAiKeyDown}
                    rows={2}
                    disabled={aiLoading}
                  />
                  <button
                    className="rs-ai-send-btn"
                    onClick={handleAiParse}
                    disabled={aiLoading || !aiCommand.trim()}
                    type="button"
                  >
                    {aiLoading ? (
                      <Loader2 size={18} className="rs-spinner" />
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                </div>
                <div className="rs-ai-examples">
                  {AI_EXAMPLE_COMMANDS.map((cmd, i) => (
                    <button
                      key={i}
                      className="rs-ai-chip"
                      onClick={() => setAiCommand(cmd)}
                      disabled={aiLoading}
                      type="button"
                    >
                      {cmd}
                    </button>
                  ))}
                </div>
                {aiExplanation && (
                  <div className="rs-ai-explanation">
                    <Sparkles size={14} />
                    <span>{aiExplanation}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rs-form-section">
            {/* Schedule Name */}
            <div className="rs-form-group">
              <label className="rs-label">Schedule Name <span className="rs-optional">(optional)</span></label>
              <input
                type="text"
                className="rs-input"
                placeholder="e.g., Weekly Team Report"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
              />
            </div>

            {/* Recipients */}
            <div className="rs-form-group">
              <label className="rs-label">Recipients</label>
              <div className="rs-email-input-row">
                <input
                  type="email"
                  className="rs-input"
                  placeholder="Enter email address"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                />
                <button className="rs-add-email-btn" onClick={addEmail} type="button">
                  <Plus size={16} />
                  Add
                </button>
              </div>
              {recipients.length > 0 && (
                <div className="rs-email-chips">
                  {recipients.map((email) => (
                    <span key={email} className="rs-email-chip">
                      {email}
                      <button onClick={() => removeEmail(email)} className="rs-chip-remove">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Frequency */}
            <div className="rs-form-group">
              <label className="rs-label">Frequency</label>
              <select
                className="rs-select"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Days of Week (weekly / biweekly) */}
            {(frequency === 'weekly' || frequency === 'biweekly') && (
              <div className="rs-form-group">
                <label className="rs-label">
                  Select Days <span className="rs-hint">(click to toggle, multi-select)</span>
                </label>
                <div className="rs-day-chips">
                  {DAYS_OF_WEEK.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`rs-day-chip ${daysOfWeek.includes(d.value) ? 'rs-day-chip-active' : ''}`}
                      onClick={() => toggleDayOfWeek(d.value)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Days of Month (monthly) */}
            {frequency === 'monthly' && (
              <div className="rs-form-group">
                <label className="rs-label">
                  Select Days of Month <span className="rs-hint">(click to toggle, multi-select)</span>
                </label>
                <div className="rs-month-day-grid">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`rs-month-day-chip ${daysOfMonth.includes(d) ? 'rs-month-day-active' : ''}`}
                      onClick={() => toggleDayOfMonth(d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Specific Dates (once / custom) */}
            {(frequency === 'once' || frequency === 'custom') && (
              <div className="rs-form-group">
                <label className="rs-label">
                  Select Dates <span className="rs-hint">(add multiple dates)</span>
                </label>
                <div className="rs-date-input-row">
                  <input
                    type="date"
                    className="rs-input"
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <button className="rs-add-email-btn" onClick={addSpecificDate} type="button">
                    <CalendarPlus size={16} />
                    Add Date
                  </button>
                </div>
                {specificDates.length > 0 && (
                  <div className="rs-date-chips">
                    {specificDates.map((date) => (
                      <span key={date} className="rs-date-chip">
                        {formatDateDisplay(date)}
                        <button onClick={() => removeSpecificDate(date)} className="rs-chip-remove">
                          <CalendarX2 size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Time & Timezone row */}
            <div className="rs-form-row">
              <div className="rs-form-group rs-form-group-half">
                <label className="rs-label">Time</label>
                <input
                  type="time"
                  className="rs-input"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                />
              </div>
              <div className="rs-form-group rs-form-group-half">
                <label className="rs-label">Timezone</label>
                <select
                  className="rs-select"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Repeat End Date (optional, for recurring types) */}
            {frequency !== 'once' && frequency !== 'custom' && (
              <div className="rs-form-group">
                <label className="rs-label">
                  End Date <span className="rs-optional">(optional - leave empty for no end)</span>
                </label>
                <input
                  type="date"
                  className="rs-input"
                  value={repeatEndDate}
                  onChange={(e) => setRepeatEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  placeholder="No end date"
                />
                {repeatEndDate && (
                  <button
                    className="rs-clear-date-btn"
                    onClick={() => setRepeatEndDate('')}
                    type="button"
                  >
                    Clear end date
                  </button>
                )}
              </div>
            )}

            {/* Error */}
            {error && <p className="rs-error">{error}</p>}

            {/* Actions */}
            <div className="rs-form-actions">
              {editingId && (
                <button className="rs-btn rs-btn-cancel" onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
              <button className="rs-btn rs-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={16} className="rs-spinner" />
                    Saving...
                  </>
                ) : editingId ? (
                  'Update Schedule'
                ) : (
                  'Create Schedule'
                )}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="rs-divider">
            <span>Existing Schedules</span>
          </div>

          {/* Schedule List */}
          <ScheduleList
            schedules={schedules}
            onToggle={handleToggle}
            onDelete={handleDelete}
            onEdit={handleEdit}
            loading={listLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportSchedulerModal;
